import { InvalidStateError, NotSupportedError, currentMedia, currentSession, editTracksInfo, getCastContext, getMediaStatus, getPlaylistSegmentFormat, isHls, setCastOptions, setPlaybackRate } from "./utils.js";
import { castFramework, ensureCastFramework, googleCastInstances } from "./registry.js";
import { RemotePlayback } from "./remote-playback.js";
import { isCaptionOrSubtitleTrack } from "@videojs/utils/dom";
//#region src/google-cast-provider.ts
/**
* Cast provider + lifecycle. Created by the {@link GoogleCastExtension} component and installed as the host's
* `targetOverride` while a cast session is connected, so its getters/setters route through the cast receiver; when
* disconnected the host falls through to the attached target. Also owns the cast framework integration, the
* `RemotePlayback` instance exposed via {@link GoogleCastProvider#remote}, and dispatches media events on the attached
* target (forwarded by the host) while casting.
*/
var GoogleCastProvider = class {
	#googleCast;
	#hooks;
	#remotePlayback;
	#isInit;
	#isCasting;
	#seeking;
	#remotePlayer;
	#remoteListeners;
	#listenersAttached;
	#playbackRate;
	#localPaused;
	#onTextTrackChange;
	#onMediaUpdate;
	constructor(googleCast) {
		this.target = null;
		this.#hooks = {};
		this.#isInit = false;
		this.#isCasting = false;
		this.#seeking = false;
		this.#listenersAttached = false;
		this.#playbackRate = 1;
		this.#localPaused = false;
		this.#onTextTrackChange = () => this.#updateRemoteTextTrack();
		this.#onMediaUpdate = () => this.#checkPlaybackRate();
		this.#googleCast = googleCast;
		this.#remotePlayback = new RemotePlayback(this);
		googleCastInstances.add(this);
		this.onCastFrameworkAvailable();
	}
	get remote() {
		if (this.target && !this.target.disableRemotePlayback) ensureCastFramework();
		return this.#remotePlayback;
	}
	attach(target) {
		this.target = target;
		target.textTracks.addEventListener("change", this.#onTextTrackChange);
	}
	detach() {
		this.target?.textTracks.removeEventListener("change", this.#onTextTrackChange);
		this.target = null;
	}
	destroy() {
		this.detach();
		googleCastInstances.delete(this);
		currentMedia()?.removeUpdateListener(this.#onMediaUpdate);
		this.#detachRemoteListeners();
		this.#isCasting = false;
		this.#isInit = false;
	}
	/** @internal Wires up callbacks pushed from {@link RemotePlayback}; not part of the public surface. */
	bindHooks(hooks) {
		Object.assign(this.#hooks, hooks);
	}
	hasDevicesAvailable() {
		const state = getCastContext()?.getCastState();
		return !!state && state !== cast.framework.CastState.NO_DEVICES_AVAILABLE;
	}
	async requestCastSession() {
		if (this.target?.disableRemotePlayback) throw new InvalidStateError("disableRemotePlayback attribute is present.");
		await ensureCastFramework();
		if (!this.#isCastApiAvailable()) throw new NotSupportedError("The RemotePlayback API is disabled on this platform.");
		const willDisconnect = this.#isCasting;
		this.#isCasting = true;
		this.#applyCastOptions();
		this.#attachRemoteListeners();
		try {
			await getCastContext().requestSession();
		} catch (err) {
			if (!willDisconnect) this.#isCasting = false;
			if (err === "cancel") return;
			throw new Error(err);
		}
		this.#localPaused = this.target?.paused ?? true;
		this.target?.pause();
		this.muted = this.target?.muted ?? false;
		try {
			await this.load();
		} catch (err) {
			console.error(err);
		}
	}
	async load() {
		if (!this.#isCasting) {
			await this.target?.load();
			return;
		}
		if (!this.#googleCast.src) return;
		const mediaInfo = new chrome.cast.media.MediaInfo(this.#googleCast.src, this.#googleCast.contentType ?? "");
		mediaInfo.customData = this.#googleCast.customData ?? null;
		const { target } = this;
		const subtitles = [...target?.querySelectorAll("track") ?? []].filter((el) => el.src && isCaptionOrSubtitleTrack(el));
		const { Track, TrackType, TextTrackType } = chrome.cast.media;
		const activeTrackIds = [];
		if (subtitles.length) mediaInfo.tracks = subtitles.map((el, i) => {
			const trackId = i + 1;
			if (!activeTrackIds.length && el.track.mode === "showing") activeTrackIds.push(trackId);
			const track = new Track(trackId, TrackType.TEXT);
			track.trackContentId = el.src;
			track.trackContentType = "text/vtt";
			track.subtype = el.kind === "captions" ? TextTrackType.CAPTIONS : TextTrackType.SUBTITLES;
			track.name = el.label;
			track.language = el.srclang;
			return track;
		});
		mediaInfo.streamType = this.#googleCast.streamType === "live" ? chrome.cast.media.StreamType.LIVE : chrome.cast.media.StreamType.BUFFERED;
		mediaInfo.metadata = new chrome.cast.media.GenericMediaMetadata();
		mediaInfo.metadata.images = [new chrome.cast.Image(target?.poster ?? "")];
		if (await isHls(this.#googleCast.src)) {
			mediaInfo.contentType ||= "application/x-mpegURL";
			const fmt = await getPlaylistSegmentFormat(this.#googleCast.src) ?? "";
			const { HlsSegmentFormat: HS, HlsVideoSegmentFormat: HVS } = chrome.cast.media;
			if (fmt.includes("m4s") || fmt.includes("mp4")) {
				mediaInfo.hlsSegmentFormat = HS.FMP4;
				mediaInfo.hlsVideoSegmentFormat = HVS.FMP4;
			} else if (fmt.includes("ts")) {
				mediaInfo.hlsSegmentFormat = HS.TS;
				mediaInfo.hlsVideoSegmentFormat = HVS.TS;
			}
		}
		const request = new chrome.cast.media.LoadRequest(mediaInfo);
		request.currentTime = this.target?.currentTime ?? 0;
		request.autoplay = !this.#localPaused;
		request.activeTrackIds = activeTrackIds;
		await currentSession()?.loadMedia(request);
		this.target?.dispatchEvent(new Event("volumechange"));
	}
	get paused() {
		if (!this.#remotePlayer.isMediaLoaded) return this.target?.paused ?? true;
		return this.#remotePlayer.isPaused || this.ended;
	}
	get ended() {
		return this.#remotePlayer.playerState === chrome.cast.media.PlayerState.IDLE && currentMedia()?.idleReason === chrome.cast.media.IdleReason.FINISHED;
	}
	get seeking() {
		return this.#seeking;
	}
	get readyState() {
		switch (this.#remotePlayer.playerState) {
			case chrome.cast.media.PlayerState.IDLE: return 0;
			case chrome.cast.media.PlayerState.BUFFERING: return 2;
			default: return 3;
		}
	}
	get duration() {
		if (!this.#remotePlayer.isMediaLoaded) return this.target?.duration ?? NaN;
		return this.#remotePlayer.duration ?? NaN;
	}
	get currentTime() {
		if (!this.#remotePlayer.isMediaLoaded) return this.target?.currentTime ?? 0;
		return this.#remotePlayer.currentTime ?? 0;
	}
	set currentTime(value) {
		this.#remotePlayer.currentTime = value;
		this.#notifySeeking();
		this.#remotePlayer.controller?.seek();
	}
	get muted() {
		return this.#remotePlayer.isMuted;
	}
	set muted(value) {
		if (value !== this.#remotePlayer.isMuted) this.#remotePlayer.controller?.muteOrUnmute();
	}
	get volume() {
		return this.#remotePlayer.volumeLevel ?? 1;
	}
	set volume(value) {
		this.#remotePlayer.volumeLevel = +value;
		this.#remotePlayer.controller?.setVolumeLevel();
	}
	get playbackRate() {
		return currentMedia()?.playbackRate ?? 1;
	}
	set playbackRate(value) {
		setPlaybackRate(value);
	}
	async play() {
		if (!this.#remotePlayer.isMediaLoaded) {
			this.#localPaused = false;
			await this.load();
			return;
		}
		if (this.paused) {
			this.#remotePlayer.controller?.playOrPause();
			return new Promise((resolve) => {
				this.target?.addEventListener("play", () => resolve(), { once: true });
			});
		}
	}
	pause() {
		if (!this.paused) this.#remotePlayer.controller?.playOrPause();
	}
	onCastFrameworkAvailable() {
		if (!castFramework || this.#isInit) return;
		this.#isInit = true;
		this.#applyCastOptions();
		this.onCastStateChanged();
		this.#remotePlayer = new castFramework.RemotePlayer();
		new castFramework.RemotePlayerController(this.#remotePlayer);
		this.#remoteListeners = {
			[castFramework.RemotePlayerEventType.IS_CONNECTED_CHANGED]: (event) => {
				if (event?.value === true) this.#hooks.setState?.("connected");
				else {
					this.#disconnect();
					this.#hooks.setState?.("disconnected");
				}
			},
			[castFramework.RemotePlayerEventType.DURATION_CHANGED]: () => {
				this.target?.dispatchEvent(new Event("durationchange"));
			},
			[castFramework.RemotePlayerEventType.VOLUME_LEVEL_CHANGED]: () => {
				this.target?.dispatchEvent(new Event("volumechange"));
			},
			[castFramework.RemotePlayerEventType.IS_MUTED_CHANGED]: () => {
				this.target?.dispatchEvent(new Event("volumechange"));
			},
			[castFramework.RemotePlayerEventType.CURRENT_TIME_CHANGED]: () => {
				if (!this.#isCasting || !this.#remotePlayer.isMediaLoaded) return;
				this.#notifySeeked();
				this.target?.dispatchEvent(new Event("timeupdate"));
			},
			[castFramework.RemotePlayerEventType.VIDEO_INFO_CHANGED]: () => {
				this.target?.dispatchEvent(new Event("resize"));
			},
			[castFramework.RemotePlayerEventType.IS_PAUSED_CHANGED]: () => {
				this.target?.dispatchEvent(new Event(this.#isCasting && this.#remotePlayer.isPaused ? "pause" : "play"));
			},
			[castFramework.RemotePlayerEventType.PLAYER_STATE_CHANGED]: () => {
				const PS = chrome.cast.media.PlayerState;
				const state = this.#isCasting ? this.#remotePlayer.playerState : void 0;
				if (state !== PS.BUFFERING) this.#notifySeeked();
				if (state === PS.PAUSED) return;
				if (state === PS.IDLE) {
					const finished = currentMedia()?.idleReason === chrome.cast.media.IdleReason.FINISHED;
					this.target?.dispatchEvent(new Event(finished ? "ended" : "emptied"));
					return;
				}
				if (state === PS.PLAYING) this.target?.dispatchEvent(new Event("playing"));
				else if (state === PS.BUFFERING) this.target?.dispatchEvent(new Event("waiting"));
			},
			[castFramework.RemotePlayerEventType.IS_MEDIA_LOADED_CHANGED]: async () => {
				if (!this.#isCasting || !this.#remotePlayer.isMediaLoaded) return;
				await Promise.resolve();
				this.#onRemoteMediaLoaded();
			}
		};
	}
	onCastStateChanged() {
		if (!this.#isInit) return;
		const CS = cast.framework.CastState;
		const state = getCastContext().getCastState();
		if (this.#isCasting && state === CS.CONNECTING) this.#hooks.setState?.("connecting");
		this.#hooks.setAvailable?.(!!state && state !== CS.NO_DEVICES_AVAILABLE);
	}
	async onSessionStateChanged() {
		if (!this.#isInit) return;
		const { SESSION_RESUMED } = castFramework.SessionState;
		if (getCastContext().getSessionState() === SESSION_RESUMED) {
			if (this.#googleCast.src === currentMedia()?.media?.contentId) {
				this.#isCasting = true;
				this.#attachRemoteListeners();
				try {
					await getMediaStatus(new chrome.cast.media.GetStatusRequest());
				} catch (error) {
					console.error(error);
				}
				this.#remoteListeners[castFramework.RemotePlayerEventType.IS_PAUSED_CHANGED]();
				this.#remoteListeners[castFramework.RemotePlayerEventType.PLAYER_STATE_CHANGED]();
				this.target?.dispatchEvent(new Event("ratechange"));
			}
		}
	}
	#isCastApiAvailable() {
		return Boolean(globalThis.chrome?.cast?.isAvailable);
	}
	#applyCastOptions() {
		const { receiver } = this.#googleCast;
		setCastOptions(receiver ? { receiverApplicationId: receiver } : {});
	}
	#attachRemoteListeners() {
		if (this.#listenersAttached) return;
		const controller = this.#remotePlayer?.controller;
		if (!controller) return;
		for (const [type, handler] of Object.entries(this.#remoteListeners)) controller.addEventListener(type, handler);
		this.#listenersAttached = true;
	}
	#detachRemoteListeners() {
		if (!this.#listenersAttached) return;
		const controller = this.#remotePlayer?.controller;
		if (controller) for (const [type, handler] of Object.entries(this.#remoteListeners)) controller.removeEventListener(type, handler);
		this.#listenersAttached = false;
	}
	#disconnect() {
		if (!this.#isCasting) return;
		currentMedia()?.removeUpdateListener(this.#onMediaUpdate);
		this.#detachRemoteListeners();
		this.#seeking = false;
		this.#playbackRate = 1;
		this.#isCasting = false;
		if (this.target) this.target.muted = this.#remotePlayer.isMuted;
		const saved = this.#remotePlayer.savedPlayerState;
		if (saved) {
			if (this.target) this.target.currentTime = saved.currentTime;
			if (saved.isPaused === false && this.target) this.target.play();
		}
	}
	#notifySeeking() {
		this.#seeking = true;
		this.target?.dispatchEvent(new Event("seeking"));
	}
	#notifySeeked() {
		if (!this.#seeking) return;
		this.#seeking = false;
		this.target?.dispatchEvent(new Event("seeked"));
	}
	#onRemoteMediaLoaded() {
		this.#playbackRate = currentMedia()?.playbackRate ?? 1;
		currentMedia()?.addUpdateListener(this.#onMediaUpdate);
		this.#updateRemoteTextTrack();
	}
	#checkPlaybackRate() {
		const rate = currentMedia()?.playbackRate ?? 1;
		if (rate !== this.#playbackRate) {
			this.#playbackRate = rate;
			this.target?.dispatchEvent(new Event("ratechange"));
		}
	}
	async #updateRemoteTextTrack() {
		if (!this.#isCasting || !this.target) return;
		const localSubs = [...this.target.textTracks].filter(isCaptionOrSubtitleTrack);
		const matched = (this.#remotePlayer.mediaInfo?.tracks ?? []).filter(({ type }) => type === chrome.cast.media.TrackType.TEXT).flatMap(({ language, name, trackId }) => {
			const local = localSubs.find((l) => l.language === language && l.label === name);
			return local?.mode ? [{
				mode: local.mode,
				trackId
			}] : [];
		});
		const hidden = new Set(matched.filter((m) => m.mode !== "showing").map((m) => m.trackId));
		const showing = matched.find((m) => m.mode === "showing")?.trackId;
		const active = currentSession()?.getSessionObj().media[0]?.activeTrackIds ?? [];
		const next = new Set(active.filter((id) => !hidden.has(id)));
		if (showing) next.add(showing);
		if (next.size === active.length && active.every((id) => next.has(id))) return;
		try {
			await editTracksInfo(new chrome.cast.media.EditTracksInfoRequest([...next]));
		} catch (error) {
			console.error(error);
		}
	}
};
//#endregion
export { GoogleCastProvider };
